import { useCallback } from 'react'
import axios from 'axios'
import { useChatStore } from '../stores/useChatStore'
import { useArcanoStore } from '../stores/useArcanoStore'
import { ARCANA_ENABLED, PROJECTS_ENABLED } from '../constants/featureFlags'
import { useCallStore } from '../stores/useCallStore'
import { useAuthContext } from '../contexts/AuthContext'
import { getConversations } from '../services/chatService'
import { getArcanos } from '../services/arcanosService'
import { getUserPreferredArcano, setUserPreferredArcano } from '../services/userService'
import { useShallow } from 'zustand/react/shallow'

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

export const useConversationManagement = () => {
  const { token, logout } = useAuthContext()
  const {
    setConversations,
    setSelectedConversation,
    setMessages,
    setIsLoadingConversations,
    setIsLoadingMessages,
    setError,
    setLastTokenUsage,
    setCurrentThinkingStatus,
    searchQuery,
  } = useChatStore(
    useShallow(state => ({
      setConversations: state.setConversations,
      setSelectedConversation: state.setSelectedConversation,
      setMessages: state.setMessages,
      setIsLoadingConversations: state.setIsLoadingConversations,
      setIsLoadingMessages: state.setIsLoadingMessages,
      setError: state.setError,
      setLastTokenUsage: state.setLastTokenUsage,
      setCurrentThinkingStatus: state.setCurrentThinkingStatus,
      searchQuery: state.searchQuery,
    }))
  )

  const {
    selectedArcano,
    selectedProject,
    setArcanos,
    setSelectedArcano,
    setSelectedProject,
    setArcanoRestorationComplete,
    selectArcanoAndClearProject,
    selectProjectAndClearArcano,
  } = useArcanoStore(
    useShallow(state => ({
      selectedArcano: state.selectedArcano,
      selectedProject: state.selectedProject,
      setArcanos: state.setArcanos,
      setSelectedArcano: state.setSelectedArcano,
      setSelectedProject: state.setSelectedProject,
      setArcanoRestorationComplete: state.setArcanoRestorationComplete,
      selectArcanoAndClearProject: state.selectArcanoAndClearProject,
      selectProjectAndClearArcano: state.selectProjectAndClearArcano,
    }))
  )

  const fetchConversations = useCallback(
    async (manualArcano, manualProject) => {
      if (!token) return

      let effectiveArcano = selectedArcano
      if (manualArcano !== undefined) {
        effectiveArcano = manualArcano
      }

      let effectiveProject = selectedProject
      if (manualProject !== undefined) {
        effectiveProject = manualProject
      }

      const arcanaIdFilter =
        ARCANA_ENABLED && effectiveArcano ? effectiveArcano.arcana_id : 'general'
      const projectIdFilter =
        PROJECTS_ENABLED && effectiveProject
          ? effectiveProject.id || effectiveProject._id
          : null

      setIsLoadingConversations(true)

      try {
        const data = await getConversations(
          token,
          searchQuery,
          arcanaIdFilter,
          projectIdFilter
        )
        setConversations(data)
      } catch (err) {
        console.error('Error fetching conversations:', err)
        setError("Impossibile caricare l'elenco delle conversazioni.")

        if (err.response?.status === 401 || err.response?.status === 403) {
          logout()
        }
      } finally {
        setIsLoadingConversations(false)
      }
    },
    [
      token,
      selectedArcano,
      selectedProject,
      searchQuery,
      setConversations,
      setIsLoadingConversations,
      setError,
      logout,
    ]
  )

  const fetchArcanos = useCallback(async () => {
    if (!token || !ARCANA_ENABLED) return
    try {
      const arcanosData = await getArcanos(token)
      setArcanos(arcanosData)
    } catch (err) {
      console.error('Error fetching arcanos:', err)
    }
  }, [token, setArcanos])

  const initializeConversations = useCallback(async () => {
    if (!ARCANA_ENABLED) {
      setSelectedArcano(null)
      setArcanoRestorationComplete(true)
      await fetchConversations()
      return
    }
    await fetchArcanos()
    await fetchConversations()

    const currentArcanos = useArcanoStore.getState().arcanos
    if (currentArcanos.length > 0) {
      try {
        const targetArcanoId = await getUserPreferredArcano(token)
        if (targetArcanoId) {
          const existingArcano = currentArcanos.find(
            a => a.arcana_id === targetArcanoId
          )
          if (existingArcano) {
            setSelectedArcano(existingArcano)
          }
        }
      } catch (err) {
        console.error('Failed to fetch preferred arcano:', err)
      } finally {
        setArcanoRestorationComplete(true)
      }
    } else {
      setArcanoRestorationComplete(true)
    }
  }, [
    token,
    fetchArcanos,
    fetchConversations,
    setSelectedArcano,
    setArcanoRestorationComplete,
  ])

  // ... (handleSelectConversation remains the same) ...
  const handleSelectConversation = useCallback(
    async conversationSummary => {
      if (!token || !conversationSummary) return

      setSelectedConversation(conversationSummary)
      setMessages([])
      setError('')
      setLastTokenUsage(null)
      setCurrentThinkingStatus('')
      setIsLoadingMessages(true)

      try {
        const response = await axios.get(
          `${API_BASE_URL}/chat/conversations/${conversationSummary.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )

        const loadedMessages = (response.data.messages || []).map(msg => {
          if (msg.metadata?.visual_explanation?.items) {
            const items = msg.metadata.visual_explanation.items.map(item => {
              if (
                item.status === 'ready' &&
                !item.image_url &&
                !item.image_b64
              ) {
                return { ...item, status: 'expired' }
              }
              return item
            })
            return {
              ...msg,
              metadata: {
                ...msg.metadata,
                visual_explanation: {
                  ...msg.metadata.visual_explanation,
                  items,
                },
              },
            }
          }
          return msg
        })
        setMessages(loadedMessages)

        if (
          ARCANA_ENABLED &&
          conversationSummary.active_arcana_id
        ) {
          const currentArcanos = useArcanoStore.getState().arcanos
          const arcano = currentArcanos.find(
            a => a.arcana_id === conversationSummary.active_arcana_id
          )
          if (arcano && arcano.arcana_id !== selectedArcano?.arcana_id) {
            setSelectedArcano(arcano)
          }
        } else if (ARCANA_ENABLED && selectedArcano !== null) {
          // It's a general conversation, clear arcano view?
          // Typically we might NOT want to clear context just by clicking a chat,
          // but if your logic requires it:
          // setSelectedArcano(null)
        }

        if (
          PROJECTS_ENABLED &&
          conversationSummary.project_id
        ) {
          setSelectedProject({ id: conversationSummary.project_id })
        }
        // Note: Don't forcefully clear project if null, as user might be viewing a project
        // and clicked a general chat (though usually lists are filtered)
      } catch (err) {
        console.error('Error fetching messages:', err)
        setError(`Impossibile caricare i messaggi per ${conversationSummary.title}.`)
        setSelectedConversation(null)
        if (err.response?.status === 401 || err.response?.status === 403) {
          logout()
        }
      } finally {
        setIsLoadingMessages(false)
      }
    },
    [
      token,
      selectedArcano,
      setSelectedConversation,
      setMessages,
      setError,
      setLastTokenUsage,
      setCurrentThinkingStatus,
      setIsLoadingMessages,
      setSelectedArcano,
      setSelectedProject,
      logout,
    ]
  )

  // 2. UPDATE: Pass args to fetchConversations
  const handleArcanoSelect = useCallback(
    async arcano => {
      selectArcanoAndClearProject(arcano)

      if (token) {
        await setUserPreferredArcano(token, arcano ? arcano.arcana_id : null)
      }

      // Pass the specific arcano (and null project) to fetch immediately
      await fetchConversations(arcano, null)

      useChatStore.getState().resetChatState()
    },
    [token, selectArcanoAndClearProject, fetchConversations]
  )

  const handleClearArcano = useCallback(async () => {
    setSelectedArcano(null)
    if (token) {
      await setUserPreferredArcano(token, null)
    }
    // Pass null explicitly
    await fetchConversations(null, undefined)
  }, [token, setSelectedArcano, fetchConversations])

  // 3. UPDATE: Fix handleProjectSelect
  const handleProjectSelect = useCallback(
    async project => {
      // 1. Update Store
      selectProjectAndClearArcano(project)

      // 2. Fetch using the PASSED project argument immediately
      //    (manualArcano=null, manualProject=project)
      await fetchConversations(null, project)

      // 3. Reset Chat Interface
      useChatStore.getState().resetChatState()
    },
    [selectProjectAndClearArcano, fetchConversations]
  )

  const handleContinueInChat = useCallback(async () => {
    const callSavedConversationId = useCallStore.getState().callSavedConversationId
    if (!callSavedConversationId || !token) return

    try {
      await fetchConversations()
      const data = await getConversations(
        token,
        searchQuery,
        selectedArcano ? selectedArcano.arcana_id : 'general'
      )
      const savedConv = data.find(c => c.id === callSavedConversationId)
      if (savedConv) {
        await handleSelectConversation(savedConv)
      }
      useCallStore.getState().setShowContinueInChat(false)
      useCallStore.getState().setCallSavedConversationId(null)
    } catch (err) {
      console.error('Error loading saved conversation:', err)
      setError('Impossibile caricare la conversazione salvata.')
    }
  }, [
    token,
    searchQuery,
    selectedArcano,
    fetchConversations,
    handleSelectConversation,
    setError,
  ])

  const handleClearFilters = useCallback(async () => {
    setSelectedArcano(null)
    setSelectedProject(null)
    if (token) {
      await setUserPreferredArcano(token, null)
    }
    await fetchConversations(null, null)
  }, [token, setSelectedArcano, setSelectedProject, fetchConversations])
  return {
    initializeConversations,
    fetchConversations,
    handleSelectConversation,
    handleArcanoSelect,
    handleClearArcano,
    handleProjectSelect,
    handleContinueInChat,
    handleClearFilters,
  }
}
